import { ExternalLink, Phone, SkipForward, Zap } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Callout, Spinner } from "@/components/ui/feedback";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { leadName } from "@/lib/campaign";
import {
  attemptsText, leadDetails, leadPlace, recentDialedLead, secondsLeft,
} from "@/lib/dialer";
import { secondsSince, stageText } from "@/lib/manual-call";
import {
  useCallDispositions, useDialedLead, useDialerCall, useDialerOffer, useOfferAction,
} from "@/lib/queries";
import type { DialerCall, DialerOffer } from "@/lib/types";
import { useNow } from "@/lib/use-now";
import { cn, formatDuration, formatPhoneNumber } from "@/lib/utils";
import { useCampaignAnswers } from "@/softphone/acd-call";
import { useRepAvailability } from "@/softphone/availability";
import { useSoftphone } from "@/softphone/softphone-context";

/**
 * The dialer on the rep's phone (ADR-019): the lead it offers in preview, and the lead it is
 * calling for them.
 *
 * In the dock, not on the Phone page, for the reason every phone control is there: an offer lasts
 * about a minute, and the rep may be on any screen when it comes. The dock opens itself for an
 * offer as it does for a ringing call.
 */

/** What the dialer is doing with this rep: the lead it offers, and whether it is calling one. */
export function useDialerWork(): { offer: DialerOffer | null; dialing: boolean } {
  const { user } = useAuth();
  const { data: offer } = useDialerOffer({ enabled: Boolean(user?.sip_endpoint_id) });
  const dialing = useRepAvailability()?.view.state === "dialing";
  return { offer: offer ?? null, dialing };
}

/**
 * A preview offer — who the lead is, how often they have been called, the time left to choose —
 * with Dial and Skip; then, once dialed, or in power mode where the dialer does not ask, the same
 * lead while the dialer calls them (`Calling`).
 *
 * A lead the dialer reaches is held for this rep only for the campaign's abandon window, two
 * seconds by default, before the call is abandoned. The ACD rings the rep's phone at that moment,
 * not at Dial, so a rep who has to click Answer misses most of them: while neither this browser's
 * auto-answer nor the campaign's is on, the view says so and offers to turn the browser's on.
 */
export function DialerView() {
  const phone = useSoftphone();
  const answeredForYou = useCampaignAnswers();
  const { offer } = useDialerWork();
  const act = useOfferAction();
  const now = useNow(offer?.expires_at ? 1_000 : null);
  // The lead just dialed. Its offer has left the server by the time the dialer is calling them, so
  // `useOfferAction` keeps it — in the query cache, where closing the phone does not lose it.
  const dialedLead = useDialedLead();
  const [error, setError] = React.useState<string | null>(null);

  const done = act.isSuccess ? act.variables : undefined;
  const dialed =
    offer === null ||
    (done?.action === "dial" && done.offer.campaign_lead_id === offer.campaign_lead_id);
  const skipped =
    done?.action === "skip" && done.offer.campaign_lead_id === offer?.campaign_lead_id;

  const run = (action: "dial" | "skip", chosen: DialerOffer) => {
    setError(null);
    act.mutate(
      { action, offer: chosen },
      {
        onError: (e) =>
          setError(
            e instanceof ApiError
              ? e.message
              : action === "dial"
                ? "Could not dial this lead."
                : "Could not skip this lead.",
          ),
      },
    );
  };

  const autoAnswerHint = !phone.autoAnswer && !answeredForYou && (
    <Callout tone="warning">
      A lead the dialer reaches is held for you for only a moment. With auto-answer on, the call
      connects without a click.
      <Button
        variant="outline"
        size="sm"
        className="mt-2 flex"
        onClick={() => phone.setAutoAnswer(true)}
      >
        <Zap />
        Turn on auto-answer
      </Button>
    </Callout>
  );

  if (dialed) {
    return <Calling chosen={offer ?? recentDialedLead(dialedLead, now)} hint={autoAnswerHint} />;
  }

  const left = secondsLeft(offer.expires_at, now);

  return (
    <div className="flex flex-col gap-3">
      <LeadSummary
        item={offer}
        corner={
          left !== null && (
            <span
              className={cn(
                "shrink-0 rounded-md px-1.5 py-0.5 font-mono text-xs tabular",
                left <= 10 ? "bg-warning-2 text-warning-11" : "bg-surface-sunken text-text-muted",
              )}
              title="Time left to choose"
            >
              {formatDuration(left)}
            </span>
          )
        }
      />

      {skipped ? (
        <p role="status" className="text-center text-xs text-text-muted">
          Skipped. The dialer offers you the next lead when it has one.
        </p>
      ) : left === 0 ? (
        <p role="status" className="text-center text-xs text-text-muted">
          This offer has ended, and the lead goes back to the list.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            className="h-11 text-sm"
            disabled={act.isPending}
            loading={act.isPending && act.variables?.action === "skip"}
            onClick={() => run("skip", offer)}
          >
            <SkipForward />
            Skip
          </Button>
          <Button
            variant="default"
            className="h-11 text-sm"
            disabled={act.isPending}
            loading={act.isPending && act.variables?.action === "dial"}
            onClick={() => run("dial", offer)}
          >
            <Phone />
            Dial
          </Button>
        </div>
      )}

      {autoAnswerHint}
      {error && <Callout tone="danger">{error}</Callout>}
    </div>
  );
}

/**
 * The dialer calling a lead for this rep, until the lead is brought to their phone: the lead as an
 * offer shows them, and how far the call has got.
 *
 * The lead is the server's (`GET /dialer/current`), because power mode offers nothing first. After
 * a preview Dial, the offer the rep `chosen` names them until the dialer has written the call.
 */
function Calling({ chosen, hint }: { chosen: DialerOffer | null; hint: React.ReactNode }) {
  const { user } = useAuth();
  const { data: call } = useDialerCall({ enabled: Boolean(user?.sip_endpoint_id) });
  const now = useNow(call ? 1_000 : null);
  const shown: LeadItem | null = call ?? chosen;
  // Before the dialer has written the call there is no stage to read: the call is being placed.
  const stage = call?.stage ?? "requested";
  const seconds = call ? secondsSince(call.started_at, now) : 0;

  return (
    <div className="flex flex-col gap-3">
      {shown && (
        <LeadSummary
          item={shown}
          corner={
            seconds > 0 && (
              <span
                className="shrink-0 rounded-md bg-surface-sunken px-1.5 py-0.5 font-mono text-xs tabular text-text-muted"
                title="Time since the dialer called them"
              >
                {formatDuration(seconds)}
              </span>
            )
          }
        />
      )}
      <div className="flex items-center gap-3" role="status" aria-live="polite">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-accent-3 text-accent-11">
          <Spinner className="size-4" />
        </span>
        <div className="min-w-0">
          <p className="truncate text-base font-semibold text-text">
            {shown ? stageText(stage) : "Calling a lead"}
          </p>
          {stage !== "answered" && (
            <p className="text-xs text-text-muted">Your phone rings the moment they answer.</p>
          )}
        </div>
      </div>
      {hint}
    </div>
  );
}

/** What an offer and a call being placed both say about their lead. */
type LeadItem = Pick<
  DialerCall,
  "campaign_id" | "campaign_name" | "attempts" | "last_disposition" | "lead"
>;

/**
 * The lead as the dialer shows them, offered or being called: the campaign, the name and number,
 * how often they have been called and how the last call went, where they are, their record, and a
 * few of the list's own columns. `corner` sits beside the name: the time left to choose, or how
 * long the call has been going.
 */
function LeadSummary({ item, corner }: { item: LeadItem; corner: React.ReactNode }) {
  const { lead } = item;
  const outcomes = useCallDispositions(
    item.campaign_id,
    item.campaign_id !== null && item.last_disposition !== null,
  );
  const name = leadName(lead);
  const place = leadPlace(lead);
  const details = leadDetails(lead.attributes);
  const label = (slug: string) => outcomes.data?.find((d) => d.slug === slug)?.label;

  return (
    <>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-xs font-medium text-text-muted">
            {item.campaign_name ?? "A lead for you"}
          </p>
          <p className="truncate text-lg font-semibold text-text">
            {name ?? formatPhoneNumber(lead.phone)}
          </p>
          {name && (
            <p className="truncate font-mono text-xs text-text-muted">
              {formatPhoneNumber(lead.phone)}
            </p>
          )}
        </div>
        {corner}
      </div>

      <p className="flex flex-wrap items-center gap-x-2 text-xs text-text-muted">
        <span>
          {attemptsText(item, label)}
          {place && ` · ${place}`}
        </span>
        {/* The record — the timeline, the notes — before the rep is talking to them (ADR-044). */}
        <Link to={`/contacts/${lead.id}`} className="inline-flex items-center gap-1 font-medium text-accent-11 hover:underline">
          <ExternalLink className="size-3" aria-hidden />
          Open record
        </Link>
      </p>

      {details.length > 0 && (
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-lg border border-border px-3 py-2 text-xs">
          {details.map(([key, value]) => (
            <React.Fragment key={key}>
              <dt className="capitalize text-text-muted">{key}</dt>
              <dd className="min-w-0 truncate text-text">{value}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}
    </>
  );
}
